// src/pages/BillDetailPage.js

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import './BillDetailPage.css';

export default function BillDetailPage() {
  const { billId } = useParams();
  const navigate = useNavigate();

  const [bill, setBill] = useState(null);
  const [items, setItems] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchBillAndItems = async () => {
    try {
      const resp = await api.get(`/bills/${billId}`);
      setBill(resp.data.bill);
      setItems(resp.data.items);
    } catch (err) {
      console.error('Error fetching bill:', err);
      navigate('/bills');
    }
  };

  const fetchAvailableItems = async () => {
    try {
      const resp = await api.get('/items');
      setAvailableItems(resp.data);
      if (resp.data.length > 0) {
        setSelectedItemId(resp.data[0].ItemID.toString());
      }
    } catch (err) {
      console.error('Error fetching item catalog:', err);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchBillAndItems();
      await fetchAvailableItems();
      setLoading(false);
    })();
  }, [billId]);

  if (loading) return <div className="page-container">Loading…</div>;
  if (!bill) return <div className="page-container">Bill not found.</div>;

  const runningSubtotal = items.reduce((sum, i) => sum + parseFloat(i.Subtotal || 0), 0);

  const handleAddItem = async (e) => {
    e.preventDefault();
    const selId = parseInt(selectedItemId, 10);
    const qtyToAdd = parseInt(quantity, 10);

    if (isNaN(selId) || selId < 1) {
      alert('Invalid item selection.');
      return;
    }
    if (isNaN(qtyToAdd) || qtyToAdd < 1) {
      alert('Quantity must be at least 1.');
      return;
    }

    const existingLine = items.find((li) => Number(li.ItemID) === selId);

    try {
      if (existingLine) {
        const newQuantity = existingLine.Quantity + qtyToAdd;
        await api.patch(`/bills/${billId}/items/${existingLine.BillItemID}`, {
          quantity: newQuantity,
        });
      } else {
        await api.post(`/bills/${billId}/items`, {
          itemId: selId,
          quantity: qtyToAdd,
        });
      }
      setQuantity(1);
      await fetchBillAndItems();
    } catch (err) {
      console.error('Error in handleAddItem:', err);
      const serverMsg =
        err.response?.data?.error ||
        err.response?.data ||
        err.message ||
        'Unknown error';
      alert(`Failed to add/update item:\n${JSON.stringify(serverMsg)}`);
    }
  };

  return (
    <div className="page-container">
      <button className="btn-back" onClick={() => navigate(-1)}>
        ← Back
      </button>

      <header className="bill-header">
        <h2>Bill #{bill.BillID}</h2>
        <div className="bill-info">
          <span className={`status-badge status-${bill.Status}`}>
            {bill.Status.charAt(0).toUpperCase() + bill.Status.slice(1)}
          </span>
          <span className="bill-date">
            {new Date(bill.CreatedAt).toLocaleString()}
          </span>
        </div>
      </header>

      {(bill.Status === 'draft' || bill.Status === 'open') && (
        <form onSubmit={handleAddItem} className="add-item-form">
          <div className="form-row">
            <label htmlFor="selectItem">Item:</label>
            <select
              id="selectItem"
              value={selectedItemId}
              onChange={(e) => setSelectedItemId(e.target.value)}
              required
              className="input-select"
            >
              {availableItems.map((i) => (
                <option key={i.ItemID} value={i.ItemID}>
                  {i.Name} (₹{parseFloat(i.Price).toFixed(2)})
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <label htmlFor="qtyInput">Quantity:</label>
            <input
              id="qtyInput"
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              required
              className="input-number"
            />
          </div>

          <button type="submit" className="btn-add">
            + Add to Bill
          </button>
        </form>
      )}

      <section className="line-items-section">
        <h3 className="section-title">Items</h3>
        {items.length === 0 ? (
          <p className="no-items">No items added yet.</p>
        ) : (
          <div className="table-wrapper">
            <table className="items-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Subtotal</th>
                  {(bill.Status === 'draft' || bill.Status === 'open') && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((li) => (
                  <tr key={li.BillItemID}>
                    <td>{li.ItemName}</td>
                    <td>{li.Quantity}</td>
                    <td>₹{parseFloat(li.UnitPrice).toFixed(2)}</td>
                    <td>₹{parseFloat(li.Subtotal).toFixed(2)}</td>
                    {(bill.Status === 'draft' || bill.Status === 'open') && (
                      <td>
                        <button
                          className="btn-delete"
                          onClick={async () => {
                            if (window.confirm(`Remove ${li.ItemName} from bill?`)) {
                              try {
                                await api.delete(`/bills/${billId}/items/${li.BillItemID}`);
                                await fetchBillAndItems();
                              } catch (err) {
                                console.error('Error deleting item:', err);
                                const msg =
                                  err.response?.data?.error ||
                                  err.response?.data ||
                                  err.message ||
                                  'Unknown error';
                                alert(`Failed to delete item:\n${JSON.stringify(msg)}`);
                              }
                            }
                          }}
                        >
                          ❌
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Total + Buttons Section (replaces footer) */}
      <div className="bill-summary-section">
        <div className="total-container">
          <span className="total-label">Total:</span>
          <span className="total-value">
            ₹
            {bill.Status === 'paid'
              ? parseFloat(bill.Total || 0).toFixed(2)
              : runningSubtotal.toFixed(2)}
          </span>
        </div>

        <div className="action-buttons">
          {bill.Status === 'open' && (
            <button
              className="btn-primary"
              onClick={async () => {
                const tax = prompt('Enter tax amount:', '0');
                const discount = prompt('Enter discount amount:', '0');
                const defaultTotal =
                  runningSubtotal +
                  parseFloat(tax || 0) -
                  parseFloat(discount || 0);
                const total = prompt('Enter final total:', defaultTotal.toFixed(2));
                const paymentMode = prompt('Payment mode (Cash/Card/UPI):', 'Cash');
                if (
                  tax != null &&
                  discount != null &&
                  total != null &&
                  paymentMode
                ) {
                  try {
                    await api.patch(`/bills/${billId}/finalize`, {
                      tax: parseFloat(tax),
                      discount: parseFloat(discount),
                      total: parseFloat(total),
                      paymentMode,
                    });
                    await fetchBillAndItems();
                  } catch (err) {
                    console.error('Error finalizing bill:', err);
                    const msg =
                      err.response?.data?.error ||
                      err.response?.data ||
                      err.message ||
                      'Unknown error';
                    alert(`Failed to finalize:\n${JSON.stringify(msg)}`);
                  }
                }
              }}
            >
              Finalize Bill
            </button>
          )}

          {(bill.Status === 'draft' || bill.Status === 'open') && (
            <button
              className="btn-secondary"
              onClick={async () => {
                if (window.confirm('Cancel this bill?')) {
                  try {
                    await api.patch(`/bills/${billId}/cancel`);
                    await fetchBillAndItems();
                  } catch (err) {
                    console.error('Error cancelling bill:', err);
                    const msg =
                      err.response?.data?.error ||
                      err.response?.data ||
                      err.message ||
                      'Unknown error';
                    alert(`Failed to cancel:\n${JSON.stringify(msg)}`);
                  }
                }
              }}
            >
              Cancel Bill
            </button>
          )}

          {bill.Status === 'paid' && (
            <button
              className="btn-primary"
              onClick={() => navigate(`/bills/${billId}/print`)}
            >
              Print Receipt 
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
